import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { CreateProductDto, api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ProductFormProps {
  onSuccess: () => void;
}

export const ProductForm: React.FC<ProductFormProps> = ({
  onSuccess,
}) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateProductDto>({
    defaultValues: {
      name: '',
      description: '',
      purchasePrice: 0,
      salePrice: 0,
      quantity: 0,
      code: '',
      companyId: user?.companyId,
    },
  });

  const onSubmit = async (data: CreateProductDto) => {
    try {
      await api.createProduct(data);
      reset();
      onSuccess();
      window.location.reload();
    } catch (error) {
      console.error('Failed to create product:', error);
    }
  };

  const text = {
    fr: {
      productCode: 'Code du produit',
      productName: 'Nom du produit',
      description: 'Description',
      purchasePrice: 'Prix d\'achat',
      salePrice: 'Prix de vente',
      quantity: 'Quantité',
      save: 'Enregistrer',
      saving: 'Enregistrement...',
      cancel: 'Annuler'
    },
    en: {
      productCode: 'Product Code',
      productName: 'Product Name',
      description: 'Description',
      purchasePrice: 'Purchase Price',
      salePrice: 'Sale Price',
      quantity: 'Quantity',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel'
    }
  };

  const currentText = text[language as keyof typeof text];

  return (
    <Dialog open={true} maxWidth="sm" fullWidth>
      <DialogTitle>{currentText.productName}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="code"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={currentText.productCode}
                  error={!!errors.code}
                  fullWidth
                  required
                />
              )}
            />
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={currentText.productName}
                  error={!!errors.name}
                  fullWidth
                  required
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={currentText.description}
                  multiline
                  rows={3}
                  fullWidth
                />
              )}
            />
            <Controller
              name="purchasePrice"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={currentText.purchasePrice}
                  type="number"
                  error={!!errors.purchasePrice}
                  fullWidth
                  required
                  inputProps={{ min: 0 }}
                />
              )}
            />
            <Controller
              name="salePrice"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={currentText.salePrice}
                  type="number"
                  error={!!errors.salePrice}
                  fullWidth
                  required
                  inputProps={{ min: 0 }}
                />
              )}
            />
            <Controller
              name="quantity"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={currentText.quantity}
                  type="number"
                  error={!!errors.quantity}
                  fullWidth
                  required
                  inputProps={{ min: 0 }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.location.reload()}>
            {currentText.cancel}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ bgcolor: '#1F2937' }}
          >
            {isSubmitting ? currentText.saving : currentText.save}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
